<template>
  <PublicLayout page-title="Política de Cookies">
    <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
      <header class="text-center mb-12">
        <h1 class="text-3xl md:text-4xl font-bold text-legal-900 mb-4 tracking-tight">Política de Cookies</h1>
        <p class="text-lg text-legal-600 max-w-2xl mx-auto leading-relaxed">
          Información sobre el uso de cookies y tecnologías similares en nuestro sitio web
        </p>
      </header>

      <article class="space-y-12">
        <section>
          <h2 class="text-3xl font-bold text-legal-900 mb-6 tracking-wide">1. ¿Qué son las cookies?</h2>
          
          <div class="bg-white rounded-2xl shadow-lg p-6 mb-6 border border-slate-200">
            <p class="text-legal-600 leading-relaxed mb-4">
              Las cookies son pequeños archivos de texto que se almacenan en su dispositivo (ordenador, tablet, móvil) 
              cuando visita nuestro sitio web. Contienen información que se utiliza para reconocer su dispositivo 
              en visitas posteriores y mejorar su experiencia de navegación.
            </p>
            <div class="grid gap-4 md:grid-cols-2">
              <div>
                <h4 class="font-medium text-legal-900 mb-2">Características Técnicas</h4>
                <ul class="space-y-1 text-legal-600 text-sm">
                  <li>• <strong>Tamaño:</strong> Máximo 4KB de datos</li>
                  <li>• <strong>Almacenamiento:</strong> Local en su dispositivo</li>
                  <li>• <strong>Acceso:</strong> Solo por el dominio que las creó</li>
                  <li>• <strong>Seguridad:</strong> Encriptadas cuando es necesario</li>
                </ul>
              </div>
              <div>
                <h4 class="font-medium text-legal-900 mb-2">Finalidades Principales</h4>
                <ul class="space-y-1 text-legal-600 text-sm">
                  <li>• Funcionalidad esencial del sitio</li>
                  <li>• Recordar preferencias del usuario</li>
                  <li>• Mantener sesiones de login</li>
                  <li>• Análisis de uso y rendimiento</li>
                </ul>
              </div>
            </div>
          </div>
          
          <div class="bg-teal-50 rounded-2xl p-6 border border-teal-100">
            <h3 class="font-medium text-legal-900 mb-4 flex items-center">
              <GlobeAltIcon class="w-5 h-5 mr-2 text-teal-600" aria-hidden="true" />
              Clasificación de Cookies
            </h3>
            <div class="grid gap-6 md:grid-cols-2">
              <div>
                <h4 class="font-medium text-legal-900 mb-3">Por Finalidad</h4>
                <div class="space-y-3">
                  <div class="bg-white rounded p-3">
                    <h5 class="font-medium text-legal-900 text-sm">Técnicas (Necesarias)</h5>
                    <p class="text-legal-600 text-xs mt-1">Imprescindibles para el funcionamiento</p>
                  </div>
                  <div class="bg-white rounded p-3">
                    <h5 class="font-medium text-legal-900 text-sm">De Preferencias</h5>
                    <p class="text-legal-600 text-xs mt-1">Recordar configuración del usuario</p>
                  </div>
                  <div class="bg-white rounded p-3">
                    <h5 class="font-medium text-legal-900 text-sm">Analíticas</h5>
                    <p class="text-legal-600 text-xs mt-1">Estadísticas anónimas de uso</p>
                  </div>
                  <div class="bg-white rounded p-3">
                    <h5 class="font-medium text-legal-900 text-sm">De Marketing</h5>
                    <p class="text-legal-600 text-xs mt-1">Publicidad personalizada <em>(No usamos)</em></p>
                  </div>
                </div>
              </div>
              <div>
                <h4 class="font-medium text-legal-900 mb-3">Por Duración</h4>
                <div class="space-y-3">
                  <div class="bg-white rounded p-3">
                    <h5 class="font-medium text-legal-900 text-sm">De Sesión</h5>
                    <p class="text-legal-600 text-xs mt-1">Se eliminan al cerrar el navegador</p>
                  </div>
                  <div class="bg-white rounded p-3">
                    <h5 class="font-medium text-legal-900 text-sm">Persistentes</h5>
                    <p class="text-legal-600 text-xs mt-1">Permanecen hasta su fecha de expiración</p>
                  </div>
                </div>
                
                <h4 class="font-medium text-legal-900 mb-3 mt-6">Por Origen</h4>
                <div class="space-y-3">
                  <div class="bg-white rounded p-3">
                    <h5 class="font-medium text-legal-900 text-sm">Propias</h5>
                    <p class="text-legal-600 text-xs mt-1">Enviadas desde olvidadme.com</p>
                  </div>
                  <div class="bg-white rounded p-3">
                    <h5 class="font-medium text-legal-900 text-sm">De Terceros</h5>
                    <p class="text-legal-600 text-xs mt-1">Enviadas desde otros dominios</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section>
          <h2 class="text-3xl font-bold text-legal-900 mb-6 tracking-wide">2. Cookies Utilizadas en Nuestro Sitio</h2>
          
          <div class="space-y-8">
            <!-- Cookies Técnicas -->
            <div class="bg-teal-50 border border-teal-200 rounded-2xl p-6">
              <h3 class="font-medium text-teal-900 mb-4 flex items-center">
                <CogIcon class="w-5 h-5 mr-2 text-teal-600" aria-hidden="true" />
                Cookies Técnicas (Siempre Activas)
              </h3>
              <p class="text-teal-800 text-sm mb-4">
                Estas cookies son imprescindibles para el funcionamiento del sitio y no pueden ser desactivadas.
              </p>
              
              <div class="overflow-x-auto">
                <table class="w-full bg-white rounded-2xl border border-slate-200 shadow-lg">
                  <thead class="bg-teal-100">
                    <tr>
                      <th class="px-4 py-2 text-left text-xs font-medium text-teal-900">Nombre</th>
                      <th class="px-4 py-2 text-left text-xs font-medium text-teal-900">Propósito</th>
                      <th class="px-4 py-2 text-left text-xs font-medium text-teal-900">Duración</th>
                      <th class="px-4 py-2 text-left text-xs font-medium text-teal-900">Tipo</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-teal-100">
                    <tr>
                      <td class="px-4 py-2 text-xs text-teal-800 font-mono">auth_token</td>
                      <td class="px-4 py-2 text-xs text-teal-700">Mantener sesión de usuario logueado</td>
                      <td class="px-4 py-2 text-xs text-teal-700">15 minutos</td>
                      <td class="px-4 py-2 text-xs text-teal-700">HTTP Only</td>
                    </tr>
                    <tr>
                      <td class="px-4 py-2 text-xs text-teal-800 font-mono">refresh_token</td>
                      <td class="px-4 py-2 text-xs text-teal-700">Renovar sesión sin relogin</td>
                      <td class="px-4 py-2 text-xs text-teal-700">7 días</td>
                      <td class="px-4 py-2 text-xs text-teal-700">HTTP Only, Secure</td>
                    </tr>
                    <tr>
                      <td class="px-4 py-2 text-xs text-teal-800 font-mono">csrf_token</td>
                      <td class="px-4 py-2 text-xs text-teal-700">Protección contra ataques CSRF</td>
                      <td class="px-4 py-2 text-xs text-teal-700">Sesión</td>
                      <td class="px-4 py-2 text-xs text-teal-700">Secure, SameSite</td>
                    </tr>
                    <tr>
                      <td class="px-4 py-2 text-xs text-teal-800 font-mono">lang_pref</td>
                      <td class="px-4 py-2 text-xs text-teal-700">Idioma preferido del usuario</td>
                      <td class="px-4 py-2 text-xs text-teal-700">30 días</td>
                      <td class="px-4 py-2 text-xs text-teal-700">Funcional</td>
                    </tr>
                    <tr>
                      <td class="px-4 py-2 text-xs text-teal-800 font-mono">cookie_consent</td>
                      <td class="px-4 py-2 text-xs text-teal-700">Registro de consentimientos dados</td>
                      <td class="px-4 py-2 text-xs text-teal-700">365 días</td>
                      <td class="px-4 py-2 text-xs text-teal-700">Persistente</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            
            <!-- Cookies Analíticas -->
            <div class="bg-white border border-slate-200 rounded-2xl p-6 shadow-lg">
              <h3 class="font-medium text-legal-900 mb-4 flex items-center">
                <ChartBarIcon class="w-5 h-5 mr-2 text-teal-600" aria-hidden="true" />
                Cookies Analíticas (Opcionales - Requieren Consentimiento)
              </h3>
              <p class="text-legal-700 text-sm mb-4">
                Nos ayudan a entender cómo interactúa con nuestro sitio para mejorarlo. Los datos se anonimizan.
              </p>
              
              <div class="overflow-x-auto">
                <table class="w-full bg-white rounded-2xl border border-slate-200 shadow-lg">
                  <thead class="bg-teal-100">
                    <tr>
                      <th class="px-4 py-2 text-left text-xs font-medium text-teal-900">Nombre</th>
                      <th class="px-4 py-2 text-left text-xs font-medium text-teal-900">Proveedor</th>
                      <th class="px-4 py-2 text-left text-xs font-medium text-teal-900">Propósito</th>
                      <th class="px-4 py-2 text-left text-xs font-medium text-teal-900">Duración</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-teal-100">
                    <tr>
                      <td class="px-4 py-2 text-xs text-teal-800 font-mono">_ga</td>
                      <td class="px-4 py-2 text-xs text-teal-700">Google Analytics</td>
                      <td class="px-4 py-2 text-xs text-teal-700">Distinguir usuarios únicos</td>
                      <td class="px-4 py-2 text-xs text-teal-700">2 años</td>
                    </tr>
                    <tr>
                      <td class="px-4 py-2 text-xs text-teal-800 font-mono">_ga_*</td>
                      <td class="px-4 py-2 text-xs text-teal-700">Google Analytics 4</td>
                      <td class="px-4 py-2 text-xs text-teal-700">Mantener estado de la sesión</td>
                      <td class="px-4 py-2 text-xs text-teal-700">2 años</td>
                    </tr>
                    <tr>
                      <td class="px-4 py-2 text-xs text-teal-800 font-mono">_gid</td>
                      <td class="px-4 py-2 text-xs text-teal-700">Google Analytics</td>
                      <td class="px-4 py-2 text-xs text-teal-700">Distinguir usuarios día a día</td>
                      <td class="px-4 py-2 text-xs text-teal-700">24 horas</td>
                    </tr>
                    <tr>
                      <td class="px-4 py-2 text-xs text-teal-800 font-mono">_gat</td>
                      <td class="px-4 py-2 text-xs text-teal-700">Google Analytics</td>
                      <td class="px-4 py-2 text-xs text-teal-700">Limitar tasa de solicitudes</td>
                      <td class="px-4 py-2 text-xs text-teal-700">1 minuto</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              
              <div class="mt-4 bg-teal-100 rounded-2xl p-3">
                <h4 class="font-medium text-teal-900 mb-2 text-sm">Configuración de Privacidad en Google Analytics</h4>
                <ul class="space-y-1 text-teal-800 text-xs">
                  <li>• <strong>Anonimización IP:</strong> Activada (se truncan las direcciones IP)</li>
                  <li>• <strong>Datos publicitarios:</strong> Deshabilitados (no se comparten datos con Google Ads)</li>
                  <li>• <strong>Retención de datos:</strong> 14 meses (mínimo legal)</li>
                  <li>• <strong>Señales de Google:</strong> Desactivadas (no cross-device tracking)</li>
                </ul>
              </div>
            </div>
            
            <!-- Cookies de Rendimiento integradas en analíticas -->
            <div class="bg-teal-100 border border-teal-200 rounded-2xl p-4 mt-4">
              <h4 class="font-medium text-teal-900 mb-2 text-sm flex items-center">
                <RocketLaunchIcon class="w-4 h-4 mr-2 text-teal-600" aria-hidden="true" />
                Métricas de Rendimiento Incluidas
              </h4>
              <p class="text-teal-800 text-xs">
                Las métricas de rendimiento del sitio se incluyen de forma anonimizada en Google Analytics para optimizar la experiencia de usuario.
              </p>
            </div>
          </div>
        </section>

        <section>
          <h2 class="text-3xl font-bold text-legal-900 mb-6 tracking-wide">3. Gestión y Control de Cookies</h2>
          
          <div class="space-y-6">
            <!-- Banner de Consentimiento -->
            <div class="bg-teal-50 border border-teal-200 rounded-2xl p-6">
              <h3 class="font-medium text-legal-900 mb-4 flex items-center">
                <BellAlertIcon class="w-5 h-5 mr-2 text-teal-600" aria-hidden="true" />
                Banner de Consentimiento
              </h3>
              <div class="grid gap-6 md:grid-cols-2">
                <div>
                  <h4 class="font-medium text-legal-900 mb-3">Características del Banner</h4>
                  <ul class="space-y-2 text-legal-600 text-sm">
                    <li>• <strong>Aparición:</strong> Primera visita al sitio</li>
                    <li>• <strong>Posición:</strong> Inferior de la pantalla</li>
                    <li>• <strong>Opciones:</strong> Aceptar todo, Rechazar, Configurar</li>
                    <li>• <strong>Información:</strong> Enlace a política completa</li>
                    <li>• <strong>Bloqueo:</strong> No impide navegación básica</li>
                  </ul>
                </div>
                <div>
                  <h4 class="font-medium text-legal-900 mb-3">Consentimiento Granular</h4>
                  <ul class="space-y-2 text-legal-600 text-sm">
                    <li>• <strong>Técnicas:</strong> Siempre activadas (no requieren consentimiento)</li>
                    <li>• <strong>Analíticas:</strong> Opción independiente</li>
                    <li>• <strong>Rendimiento:</strong> Opción independiente</li>
                    <li>• <strong>Marketing:</strong> No aplicable (no utilizamos)</li>
                  </ul>
                </div>
              </div>
            </div>
            
            <!-- Centro de Preferencias -->
            <div class="bg-white rounded-2xl p-6 border border-slate-200 shadow-lg">
              <h3 class="font-medium text-legal-900 mb-4 flex items-center">
                <CogIcon class="w-5 h-5 mr-2 text-teal-600" aria-hidden="true" />
                Centro de Preferencias de Cookies
              </h3>
              <p class="text-legal-600 text-sm mb-4">
                Puede acceder en cualquier momento a su configuración de cookies:
              </p>
              <div class="grid gap-4 md:grid-cols-2">
                <div>
                  <h4 class="font-medium text-legal-900 mb-2">Acceso al Centro</h4>
                  <ul class="space-y-1 text-legal-600 text-sm">
                    <li>• Botón flotante "Configurar Cookies"</li>
                    <li>• Enlace en el footer del sitio</li>
                    <li>• Desde su panel de usuario</li>
                    <li>• URL directa: /cookies-preferences</li>
                  </ul>
                </div>
                <div>
                  <h4 class="font-medium text-legal-900 mb-2">Funcionalidades</h4>
                  <ul class="space-y-1 text-legal-600 text-sm">
                    <li>• Ver estado actual de consentimientos</li>
                    <li>• Activar/desactivar por categorías</li>
                    <li>• Historial de cambios</li>
                    <li>• Exportar configuración</li>
                  </ul>
                </div>
              </div>
              
              <div class="mt-4 p-4 bg-white rounded border border-legal-200">
                <button class="bg-gradient-to-r from-teal-600 to-teal-500 text-white px-6 py-3 rounded-lg hover:from-teal-700 hover:to-teal-600 transition-all duration-200 transform hover:scale-105 shadow-lg text-sm font-medium">
                  Configurar Mis Cookies
                </button>
                <span class="ml-3 text-legal-500 text-sm">Abrir centro de preferencias</span>
              </div>
            </div>
            
            <!-- Derechos del Usuario -->
            <div class="bg-teal-50 border border-teal-200 rounded-2xl p-6">
              <h3 class="font-medium text-teal-900 mb-4 flex items-center">
                <CheckCircleIcon class="w-5 h-5 mr-2 text-teal-600" aria-hidden="true" />
                Sus Derechos sobre Cookies
              </h3>
              <div class="grid gap-6 md:grid-cols-2">
                <div>
                  <h4 class="font-medium text-teal-900 mb-3">Derechos Fundamentales</h4>
                  <ul class="space-y-2 text-teal-800 text-sm">
                    <li>• <strong>Consentimiento libre:</strong> No es requisito para usar el sitio</li>
                    <li>• <strong>Información previa:</strong> Conocer qué cookies se instalarán</li>
                    <li>• <strong>Consentimiento específico:</strong> Por categorías, no genérico</li>
                    <li>• <strong>Revocación:</strong> Cambiar opinión en cualquier momento</li>
                  </ul>
                </div>
                <div>
                  <h4 class="font-medium text-teal-900 mb-3">Acciones Disponibles</h4>
                  <ul class="space-y-2 text-teal-800 text-sm">
                    <li>• <strong>Aceptar selectivamente:</strong> Solo las categorías deseadas</li>
                    <li>• <strong>Rechazar todas:</strong> Solo cookies técnicas necesarias</li>
                    <li>• <strong>Modificar preferencias:</strong> Cambiar en cualquier momento</li>
                    <li>• <strong>Eliminar cookies:</strong> Borrar las ya instaladas</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section>
          <h2 class="text-3xl font-bold text-legal-900 mb-6 tracking-wide">4. Configuración por Navegador</h2>
          
          <div class="grid gap-6 md:grid-cols-2">
            <!-- Navegadores Desktop -->
            <div class="bg-white border border-slate-200 rounded-2xl p-6 shadow-lg">
              <h3 class="font-medium text-legal-900 mb-4 flex items-center">
                <ComputerDesktopIcon class="w-5 h-5 mr-2 text-teal-600" aria-hidden="true" />
                Navegadores de Escritorio
              </h3>
              
              <div class="space-y-4">
                <div class="border-b border-legal-100 pb-3">
                  <h4 class="font-medium text-legal-900 mb-2 text-sm">Google Chrome</h4>
                  <p class="text-legal-600 text-xs mb-2">Configuración → Privacidad y seguridad → Cookies y otros datos de sitios</p>
                  <ul class="text-legal-500 text-xs space-y-1">
                    <li>• "Bloquear cookies de terceros"</li>
                    <li>• "Eliminar cookies al salir"</li>
                    <li>• Gestionar excepciones por sitio</li>
                  </ul>
                </div>
                
                <div class="border-b border-legal-100 pb-3">
                  <h4 class="font-medium text-legal-900 mb-2 text-sm">Mozilla Firefox</h4>
                  <p class="text-legal-600 text-xs mb-2">Opciones → Privacidad y Seguridad → Cookies y datos del sitio</p>
                  <ul class="text-legal-500 text-xs space-y-1">
                    <li>• "Protección de rastreo mejorada"</li>
                    <li>• "Eliminar cookies al cerrar Firefox"</li>
                    <li>• Excepciones personalizadas</li>
                  </ul>
                </div>
                
                <div class="border-b border-legal-100 pb-3">
                  <h4 class="font-medium text-legal-900 mb-2 text-sm">Microsoft Edge</h4>
                  <p class="text-legal-600 text-xs mb-2">Configuración → Cookies y permisos del sitio → Cookies</p>
                  <ul class="text-legal-500 text-xs space-y-1">
                    <li>• "Bloquear cookies de terceros"</li>
                    <li>• "Borrar datos al cerrar navegador"</li>
                    <li>• Lista de sitios permitidos/bloqueados</li>
                  </ul>
                </div>
                
                <div>
                  <h4 class="font-medium text-legal-900 mb-2 text-sm">Safari</h4>
                  <p class="text-legal-600 text-xs mb-2">Preferencias → Privacidad → Cookies y datos de sitios web</p>
                  <ul class="text-legal-500 text-xs space-y-1">
                    <li>• "Prevenir seguimiento entre sitios"</li>
                    <li>• "Bloquear todas las cookies"</li>
                    <li>• Gestionar datos por sitio web</li>
                  </ul>
                </div>
              </div>
            </div>
            
            <!-- Navegadores Móviles -->
            <div class="bg-white border border-slate-200 rounded-2xl p-6 shadow-lg">
              <h3 class="font-medium text-legal-900 mb-4 flex items-center">
                <DevicePhoneMobileIcon class="w-5 h-5 mr-2 text-teal-600" aria-hidden="true" />
                Navegadores Móviles
              </h3>
              
              <div class="space-y-4">
                <div class="border-b border-legal-100 pb-3">
                  <h4 class="font-medium text-legal-900 mb-2 text-sm">Chrome Móvil (Android/iOS)</h4>
                  <p class="text-legal-600 text-xs mb-2">Menú → Configuración → Configuración del sitio → Cookies</p>
                  <ul class="text-legal-500 text-xs space-y-1">
                    <li>• Activar/desactivar cookies</li>
                    <li>• Bloquear cookies de terceros</li>
                    <li>• Borrar datos de navegación</li>
                  </ul>
                </div>
                
                <div class="border-b border-legal-100 pb-3">
                  <h4 class="font-medium text-legal-900 mb-2 text-sm">Safari Móvil (iOS)</h4>
                  <p class="text-legal-600 text-xs mb-2">Ajustes → Safari → Privacidad y Seguridad</p>
                  <ul class="text-legal-500 text-xs space-y-1">
                    <li>• "Prevenir seguimiento entre sitios"</li>
                    <li>• "Bloquear todas las cookies"</li>
                    <li>• "Borrar historial y datos"</li>
                  </ul>
                </div>
                
                <div class="border-b border-legal-100 pb-3">
                  <h4 class="font-medium text-legal-900 mb-2 text-sm">Firefox Móvil</h4>
                  <p class="text-legal-600 text-xs mb-2">Menú → Configuración → Privacidad</p>
                  <ul class="text-legal-500 text-xs space-y-1">
                    <li>• Protección de rastreo</li>
                    <li>• Configuración de cookies</li>
                    <li>• Eliminar datos privados</li>
                  </ul>
                </div>
                
                <div>
                  <h4 class="font-medium text-legal-900 mb-2 text-sm">Samsung Internet</h4>
                  <p class="text-legal-600 text-xs mb-2">Menú → Configuración → Sitios web y descargas</p>
                  <ul class="text-legal-500 text-xs space-y-1">
                    <li>• "Aceptar cookies"</li>
                    <li>• Bloqueo inteligente de anuncios</li>
                    <li>• Eliminar datos de navegación</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
          
          <div class="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-6">
            <h3 class="font-medium text-yellow-900 mb-3 flex items-center">
              <ExclamationTriangleIcon class="w-5 h-5 mr-2 text-yellow-600" aria-hidden="true" />
              Advertencia Importante
            </h3>
            <p class="text-yellow-800 text-sm">
              Deshabilitar todas las cookies puede afectar la funcionalidad del sitio web. 
              Las cookies técnicas son necesarias para funciones básicas como el login de usuarios 
              y la seguridad. Recomendamos mantener activadas al menos las cookies necesarias.
            </p>
          </div>
        </section>

        <section>
          <h2 class="text-3xl font-bold text-legal-900 mb-6 tracking-wide">5. Base Legal y Cumplimiento</h2>
          
          <div class="space-y-6">
            <div class="bg-white border border-slate-200 rounded-2xl p-6 shadow-lg">
              <h3 class="font-medium text-legal-900 mb-4 flex items-center">
                <ScaleIcon class="w-5 h-5 mr-2 text-teal-600" aria-hidden="true" />
                Marco Normativo
              </h3>
              <div class="grid gap-4 md:grid-cols-2">
                <div>
                  <h4 class="font-medium text-legal-900 mb-2">Normativa Europea</h4>
                  <ul class="space-y-1 text-legal-700 text-sm">
                    <li>• <strong>RGPD Art. 6:</strong> Licitud del tratamiento</li>
                    <li>• <strong>Directiva ePrivacy:</strong> Consentimiento cookies</li>
                    <li>• <strong>EDPB Guidelines 05/2020:</strong> Consentimiento</li>
                  </ul>
                </div>
                <div>
                  <h4 class="font-medium text-legal-900 mb-2">Legislación Española</h4>
                  <ul class="space-y-1 text-legal-700 text-sm">
                    <li>• <strong>LSSI Art. 22:</strong> Cookies y consentimiento</li>
                    <li>• <strong>LOPDGDD Art. 28:</strong> Videovigilancia</li>
                    <li>• <strong>Guía AEPD:</strong> Uso de cookies</li>
                  </ul>
                </div>
              </div>
            </div>
            
            <div class="bg-white rounded-2xl p-6 border border-slate-200 shadow-lg">
              <h3 class="font-medium text-legal-900 mb-4">Principios de Cumplimiento</h3>
              <div class="grid gap-6 md:grid-cols-2">
                <div>
                  <h4 class="font-medium text-legal-900 mb-3">Consentimiento Válido</h4>
                  <ul class="space-y-2 text-legal-600 text-sm">
                    <li>• <strong>Libre:</strong> Sin condicionarlo al acceso</li>
                    <li>• <strong>Específico:</strong> Por finalidades concretas</li>
                    <li>• <strong>Informado:</strong> Con información clara</li>
                    <li>• <strong>Inequívoco:</strong> Acción afirmativa clara</li>
                    <li>• <strong>Revocable:</strong> Fácil retirada</li>
                  </ul>
                </div>
                <div>
                  <h4 class="font-medium text-legal-900 mb-3">Registro y Trazabilidad</h4>
                  <ul class="space-y-2 text-legal-600 text-sm">
                    <li>• <strong>Fecha y hora:</strong> Momento del consentimiento</li>
                    <li>• <strong>Categorías:</strong> Qué tipos fueron aceptados</li>
                    <li>• <strong>Versión:</strong> Política vigente en ese momento</li>
                    <li>• <strong>Método:</strong> Cómo se obtuvo el consentimiento</li>
                    <li>• <strong>Identificación:</strong> Usuario que dio el consentimiento</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>
      </article>
    </div>
  </PublicLayout>
</template>

<script setup>
import { useSeo } from '@/composables/useSeo'
import PublicLayout from '@/components/layout/PublicLayout.vue'

// SEO Meta Tags
useSeo({
  title: 'Política de Cookies',
  description: 'Uso de cookies en Olvidadme. Tipos, finalidad y gestión de preferencias.',
  canonical: '/politica-cookies'
})

// Heroicons imports for professional legal appearance
import {
  CogIcon,
  GlobeAltIcon,
  ChartBarIcon,
  RocketLaunchIcon,
  BellAlertIcon,
  ComputerDesktopIcon,
  DevicePhoneMobileIcon,
  ExclamationTriangleIcon,
  ScaleIcon
} from '@heroicons/vue/24/outline'
import {
  CheckCircleIcon
} from '@heroicons/vue/24/solid'

// CookiePolicy component now uses PublicLayout for consistent navigation

if (typeof window !== 'undefined' && window.gtag) {
  window.gtag('event', 'legal_page_view', {
    event_category: 'legal_compliance',
    event_label: 'cookie_policy',
    value: 1
  })
}
</script>